import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

export interface MediaListSectionProps {
  icon: LucideIcon;
  title: string;
  /** 计数;省略则不渲染括号。0 也会渲染(「历史 (0)」是有意义的信息)。 */
  count?: number;
  /** 图标着色(节的语义色):进行中=warning、草稿=info、历史=success。 */
  iconClassName?: string;
  /** 图标是否转(进行中节)。 */
  spin?: boolean;
  /** 标题行右侧槽:筛选 chips / 搜索框 / 批量下载等**只作用于本节**的控件。 */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** 落在 <section> 上的 data-testid(页面测试按节断言用)。 */
  testId?: string;
}

/**
 * 媒体列表页的分节骨架(2026-07-16 三线布局统一)。
 *
 * <p>三线(海报/视频/分身)统一成「进行中 / 草稿 / 历史」三段式,每段一个本组件。
 *
 * <p>★<b>actions 槽的语义是「只作用于本节」</b>:状态 chips 和搜索框放在**历史**节的标题行,
 * 因为它们只筛历史(在途任务由顶部节独立拉取,不受筛选影响)。作用于整页的控件(组织归属筛选、
 * 创建按钮)属于 <PageHeader>,别塞进来 —— 位置错了用户就会以为搜索框也搜在途任务。
 */
export function MediaListSection({
  icon: Icon,
  title,
  count,
  iconClassName,
  spin = false,
  actions,
  children,
  className,
  testId,
}: MediaListSectionProps) {
  return (
    <section className={className} data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Icon className={cn("h-4 w-4", spin && "animate-spin", iconClassName)} />
          {title}
          {count !== undefined && (
            <span className="font-normal text-muted-foreground">({count})</span>
          )}
        </h2>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
